import React, { useState, useEffect } from 'react';
import './ContentTypeBuilder.css';
import { buildApiUrl } from '../../config/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Single Line Text' },
  { value: 'textarea', label: 'Multi-line Text' },
  { value: 'richText', label: 'Rich Text Editor (Formatting Tools)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'image', label: 'Image URL' },
  { value: 'cart_name', label: 'Cart Name' },
  { value: 'cart_price', label: 'Cart Price' },
  { value: 'cart_image', label: 'Cart Image' }
];

const ContentTypeBuilder = ({ siteId, contentType, onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [fields, setFields] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [fieldSuccess, setFieldSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (contentType && contentType !== 'new') {
      console.log("Initializing ContentTypeBuilder with existing type:", contentType);
      setName(contentType.name || '');
      setFields(contentType.fields || []);
      setIsEditing(true);
    } else {
      console.log("Initializing ContentTypeBuilder for new content type");
      setName('');
      setFields([]);
      setIsEditing(false);
    }
  }, [contentType]);

  const validateFieldName = (fieldName) => {
    console.log("Validating field name:", fieldName);
    
    // Check if field name is empty
    if (!fieldName.trim()) {
      setFieldError('Field name is required');
      return false;
    }

    // Check if field name contains only alphanumeric characters and underscores
    if (!/^[a-zA-Z0-9_]+$/.test(fieldName)) {
      setFieldError('Field name can only contain letters, numbers, and underscores');
      return false;
    }

    // Check for duplicate field names
    if (fields.some(field => field.name.toLowerCase() === fieldName.toLowerCase())) {
      setFieldError('Field name must be unique');
      return false;
    }

    console.log("Field name validation passed");
    return true;
  };

  const handleAddField = () => {
    console.log("Adding new field:", newFieldName, newFieldType, newFieldRequired);
    
    if (!validateFieldName(newFieldName)) {
      setFieldSuccess('');
      return;
    }

    const newField = { 
      name: newFieldName, 
      type: newFieldType,
      required: newFieldRequired,
      label: newFieldName.charAt(0).toUpperCase() + newFieldName.slice(1).replace(/_/g, ' '),
      originalType: newFieldType
    };
    
    console.log("Created new field object:", newField);
    
    const updatedFields = [...fields, newField];
    console.log("Updated fields array:", updatedFields);
    
    setFields(updatedFields);
    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setFieldError('');
    setFieldSuccess(`Field "${newField.label}" added successfully`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setFieldSuccess('');
    }, 3000);
  };

  const handleRemoveField = (index) => {
    const fieldToRemove = fields[index];
    
    // If editing an existing content type, warn about removing fields
    if (isEditing) {
      if (!window.confirm(`Are you sure you want to remove the field "${fieldToRemove.label || fieldToRemove.name}"? This may cause data loss in existing content.`)) {
        return;
      }
    }
    
    const newFields = [...fields];
    newFields.splice(index, 1);
    setFields(newFields);
  };

  const handleSave = async () => {
    console.log("Saving content type...");
    console.log("Name:", name);
    console.log("Fields:", fields);
    
    if (!name.trim()) {
      setError('Content type name is required');
      return;
    }

    if (fields.length === 0) {
      setError('At least one field is required');
      return;
    }

    try {
      // Map custom field types to standard types
      const mappedFields = fields.map(field => {
        const mappedField = { ...field };
        
        // Map custom field types to standard types
        switch (field.type) {
          case 'cart_name':
            mappedField.type = 'text';
            break;
          case 'cart_price':
            mappedField.type = 'number';
            break;
          case 'cart_image':
            mappedField.type = 'image';
            break;
          default:
            // Keep the original type for standard types
            break;
        }
        
        return mappedField;
      });

      // If editing an existing content type
      if (contentType && contentType._id) {
        console.log("Updating existing content type:", contentType._id);
        const updatedContentType = {
          ...contentType,
          name,
          fields: mappedFields
        };
        console.log("Sending updated content type:", updatedContentType);
        onSave(updatedContentType);
        return;
      }

      // If creating a new content type
      console.log("Creating new content type");
      const requestBody = {
        name,
        fields: mappedFields,
        siteId
      };
      console.log("Request body:", requestBody);
      
      const response = await fetch(buildApiUrl('api/cms/types'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log("API response:", data);
      
      if (data.success) {
        console.log("Content type created successfully:", data.contentType);
        onSave(data.contentType);
      } else {
        console.error("Failed to create content type:", data.message);
        setError(data.message || 'Failed to create content type');
      }
    } catch (error) {
      console.error('Error saving content type:', error);
      setError('An error occurred while saving');
    }
  };

  return (
    <div className="content-type-builder">
      <div className="builder-header">
        <h3>{isEditing ? `Edit Content Type: ${contentType.name}` : 'Create New Content Type'}</h3>
        <div className="builder-actions">
          <button className="cancel-button" onClick={onCancel}>Cancel</button>
          <button className="save-button" onClick={handleSave}>Save Content Type</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label htmlFor="content-type-name">Content Type Name</label>
        <input
          id="content-type-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Blog Post, Product, Team Member"
        />
      </div>

      <div className="fields-section">
        <h4>Fields</h4>
        
        <div className="fields-list">
          {fields.length === 0 ? (
            <div className="empty-fields">
              <p>No fields added yet. Add your first field below.</p>
            </div>
          ) : (
            <ul>
              {fields.map((field, index) => (
                <li key={index} className="field-item">
                  <div className="field-info">
                    <span className="field-name">{field.label || field.name}</span>
                    <div className="field-details">
                      <span className="field-type">
                        {FIELD_TYPES.find(type => type.value === field.type)?.label || field.type}
                      </span>
                      {field.required && (
                        <span className="field-required">Required</span>
                      )}
                    </div>
                  </div>
                  <button 
                    className="remove-field-button"
                    onClick={() => handleRemoveField(index)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="add-field-form">
          <h4>Add New Field</h4>
          
          {fieldError && <div className="field-error-message">{fieldError}</div>}
          {fieldSuccess && <div className="field-success-message">{fieldSuccess}</div>}
          
          <div className="field-inputs-container">
            <div className="field-inputs-row">
              <div className="form-group">
                <label htmlFor="field-name">Field Name</label>
                <input
                  id="field-name"
                  type="text"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="e.g. title, description, price (no spaces)"
                  className={fieldError && !newFieldName ? "error-input" : ""}
                />
                <small className="field-help">Use only letters, numbers, and underscores. No spaces.</small>
              </div>
            </div>
            
            <div className="field-inputs-row field-type-row">
              <div className="form-group">
                <label htmlFor="field-type">Field Type</label>
                <select
                  id="field-type"
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value)}
                >
                  {FIELD_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group field-required-group">
                <div className="checkbox-field">
                  <input
                    type="checkbox"
                    id="field-required"
                    checked={newFieldRequired}
                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                  />
                  <label htmlFor="field-required">Required Field</label>
                </div>
              </div>
            </div>
            
            <div className="field-inputs-row">
              <button 
                className="add-field-button"
                onClick={handleAddField}
              >
                Add Field
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentTypeBuilder; 